<template>
  <div :id="`question_${index + 1}`"
       class="py-4 flex relative"
  >
    <span class="w-20 py-5 px-2 text-3xl leading-relaxed absolute transform -translate-y-3 lg:px-6 lg:translate-y-0">{{ index + 1 }}</span>
    <div :class="[
      'min-w-0 flex-1 py-3 flex flex-col gap-y-2 lg:pl-20 lg:py-5 lg:pl-14 gap-y-2',
      [ isMobile ? 'pl-20' : 'pl-10' ],
    ]">
      <template>
        <div class="-ml-20 pl-20 flex items-start gap-x-1">
          <p class="w-full rounded-lg py-2.5 px-5 bg-gray-100">{{ question__user.questionTitle }}</p>
          <div v-if="bankExam.examType === 'QUIZ'" class="flex items-center gap-x-0.5">
            <GrmTextField type="number" v-model="question__user.questionScore" class="w-20" disabled />
            <span class="text-xs">{{ $t("exam.question.point") }}</span>
          </div>
        </div>
      </template>
      <div class="flex gap-1"
           :class="[
              hasEmceeRole() ? 'flex-wrap' : 'flex-col'
           ]"
      >
        <div v-for="(answer, i) in question__user.answerList"
             :key="i"
             class="flex flex-col gap-y-1"
        >
          <div class="flex items-start">
            <p v-if="bankExam.examType === 'QUIZ' && hasEmceeRole()"
               class="bg-blue-50 rounded-lg p-4 text-sm break-all"
            >{{ answer.answerText }}</p>
            <GrmTextarea v-else-if="!hasEmceeRole()"
                         v-model="answer.answerText"
                         color="blue"
                         class="min-w-0 flex-1"
                         :placeholder="$t('exam.question.placeholder')"
                         @focusout="() => { onFocusoutAnswer(answer) }"
            />
          </div>
        </div>
      </div>
      <div v-if="hasEmceeRole()" class="flex flex-col gap-y-2">
        <div v-for="(subjectiveAnswer, subjectiveUserId) in subjectiveAnswerList"
             :key="subjectiveUserId"
             class="flex items-start gap-1"
        >
          <p class="w-max rounded px-2 bg-gray-200 text-xs text-gray-800 font-bold leading-6">{{ subjectiveAnswer.username }}</p>
          <div class="min-w-0 flex-1 flex flex-wrap gap-1">
            <div v-for="(answer, i) in subjectiveAnswer.answerList"
                 :key="i"
                 class="flex flex-col gap-y-1"
            >
              <div class="flex items-start">
                <p class="border border-blue-400 rounded-lg p-4 text-sm break-all">{{ answer.answerText }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from "vuex";
import {cloneDeep} from "lodash";

import GrmTextarea from "@/components/Common/textarea/GrmTextarea.vue";
import GrmTextField from "@/components/Common/textField/GrmTextField.vue";
import {Device} from "@/utils";

export default {
  name: 'RoomExamQuestionSubjective',
  components: {
    GrmTextarea,
    GrmTextField,
  },
  props: {
    index: { type: Number, default: 0 },
    question: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      question__user: {},
      userAnswerList: [],
    }
  },
  computed: {
    ...mapState('exam', [
      'bankExam',
      'roomExamUsersAnswerObj',
    ]),
    ...mapGetters('user', [
      'me',
      'hasEmceeRole',
    ]),

    subjectiveAnswerList() {
      const subjectiveAnswerList = {};
      const hasUserQuestionList = Object.values(this.roomExamUsersAnswerObj).find(roomExamUsersAnswer => roomExamUsersAnswer.userQuestionList && roomExamUsersAnswer.username);
      if (!hasUserQuestionList) {
        return subjectiveAnswerList;
      }

      for (const [userId, { userQuestionList, username }] of Object.entries(this.roomExamUsersAnswerObj)) {
        if (!userQuestionList) {
            continue;
        }

        for (const userQuestion of userQuestionList) {
          if (
              userQuestion.questionType !== 'SUBJECTIVE' ||
              userQuestion.questionId !== this.question.questionId
          ) {
            continue;
          }

          subjectiveAnswerList[userId] = {
            username,
            answerList: userQuestion.userAnswerList,
          };
        }

        if (subjectiveAnswerList[userId]?.answerList.length) {
          subjectiveAnswerList[userId].answerList = subjectiveAnswerList[userId].answerList
            .filter(answer => !!answer.answerText)
            .sort((a, b) => a.answerId - b.answerId);
        } else {
          delete subjectiveAnswerList[userId];
        }
      }

      return subjectiveAnswerList;
    },
    isMobile() {
      return Device.isMobile();
    }
  },
  created() {
    this.setQuestionUser();
  },
  methods: {
    ...mapActions('exam', [
      'submitUserAnswers',
    ]),

    setQuestionUser(question) {
      this.question__user = cloneDeep(this.question);
      this.question__user.answerList = this.hasEmceeRole()
          ? this.question__user.answerList
          : (() => {
            const userAnswerList = this.roomExamUsersAnswerObj[this.me.userId]?.userQuestionList?.find(userQuestion => userQuestion.questionId === this.question.questionId)?.userAnswerList || [];
            this.question__user.answerList = this.question__user.answerList.map((answer, i) => {
              answer.answerText = userAnswerList[i]?.answerText || '';

              this.userAnswerList.push({
                answerId: answer.answerId,
                answerText: answer.answerText,
              });

              return answer;
            });
            return this.question__user.answerList;
          })();
    },
    onFocusoutAnswer(answer) {
      if (this.hasEmceeRole()) {
        return;
      }

      try {
        this.onSetUserAnswerList(answer);
        this.submitUserAnswers({
          userQuestion: {
            questionId: this.question.questionId,
            questionType: this.question.questionType,
            questionScore: this.question.questionScore,
            questionOrder: this.question.questionOrder,
            userAnswerList: this.userAnswerList,
          }
        });
      } catch (e) {
        this.$alert(this.$t('exam.failError'));
      }
    },
    onSetUserAnswerList(answer) {
      if (!answer.answerText.trim()) {
        this.userAnswerList = this.userAnswerList.filter(userAnswer => userAnswer.answerId !== answer.answerId);
        return;
      }

      const userAnswer = this.userAnswerList.find(userAnswer => userAnswer.answerId === answer.answerId);
      if (userAnswer) {
        userAnswer.answerText = answer.answerText;
      } else {
        this.userAnswerList.push({
          answerId: answer.answerId,
          answerText: answer.answerText,
        });
      }
    }
  }
}
</script>